import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { Check, Close, RadioButtonUnchecked } from "@mui/icons-material";
import { API_BASE_URL } from "./SignUp";

interface InquiryListProps {
  closeInquiryListModal: () => void;
}

const InquiryList: React.FC<InquiryListProps> = ({ closeInquiryListModal }) => {
  type Reply = {
    id: number;
    content: string;
    writer: {
      accountId: string;
    };
    createdAt: string;
    updatedAt: string;
  };
  type Inquiry = {
    id: number;
    title: string;
    content: string;
    reply: Reply | null;
    createdAt: string;
    updatedAt: string;
  };
  // 문의 리스트 조회 관련 상태
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  // 문의 상세 모달 관련 상태
  const [open, setOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);

  // 문의 생성 관련 상태
  const [newInquiryTitle, setNewInquiryTitle] = useState("");
  const [newInquiryContent, setNewInquiryContent] = useState("");

  // 리스트 조회 관련 기능
  useEffect(() => {
    fetchInquiriesWithReplies();
  }, []);

  const handleClose = () => {
    setOpen(false);
    setSelectedInquiry(null);
  };

  const fetchInquiriesWithReplies = async () => {
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      alert("로그인이 필요한 서비스입니다.");
      closeInquiryListModal();
      return;
    }

    axios
      .get(`${API_BASE_URL}/inquiries/my`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setInquiries(response.data);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };
  ////////////////////

  if (loading) {
    return (
      <Container style={{ textAlign: "center", padding: "50px" }}>
        <CircularProgress />
      </Container>
    );
  }

  // 문의 상세 모달 관련 함수
  const handleClickOpen = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setOpen(true);
  };

  // 문의 생성 관련 함수
  const handleCreateInquiry = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      alert("로그인이 필요한 서비스입니다.");
      return;
    }

    setOpen(true);
  };

  const handleSubmitInquiry = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      alert("로그인이 필요한 서비스입니다.");
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/inquiries`,
        {
          title: newInquiryTitle,
          content: newInquiryContent,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // Reset the form
      setNewInquiryTitle("");
      setNewInquiryContent("");
      handleClose(); // Close modal
      fetchInquiriesWithReplies(); // Refresh the inquiry list
    } catch (error) {
      console.error("Failed to create inquiry", error);
    }
  };

  // 문의 삭제 관련 함수
  const handleDelete = async (inquiry: Inquiry) => {
    const confirmDelete = window.confirm(
      `정말 "${inquiry.title}" 문의를 삭제하시겠습니까? 답변도 함께 삭제됩니다.`
    );
    if (confirmDelete) {
      try {
        await axios.delete(`${API_BASE_URL}/inquiries/${inquiry.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        });
        fetchInquiriesWithReplies(); // 문의목록을 새로 가져옴
      } catch (error) {
        window.alert("문의 삭제 실패. 다시 시도 해 주세요.");
      }
    }
  };

  const shortenWords = (str: String, length = 24) => {
    let result: String = "";

    if (str.length > length) {
      result = str.substr(0, length - 2) + "...";
    } else {
      result = str;
    }

    return result;
  };

  return (
    <Container style={{ width: "100%", minWidth: "200px" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        minWidth="200px"
        padding="20px 0"
        sx={{
          position: "sticky",
          top: 0,
          background: "white",
          zIndex: 1,
          borderBottom: "1px solid lightgray",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ margin: "0" }}>
          문의 리스트
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateInquiry}
          sx={{ marginBottom: "10px" }}
        >
          문의 생성
        </Button>
      </Box>

      <Box
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          maxHeight: "70%",
          padding: "0 10px",
        }}
      >
        {/* 스크롤 영역 */}
        {inquiries.map((inquiry: Inquiry) => (
          <TableContainer
            component={Paper}
            sx={{
              margin: "10px 0 30px 0",
              boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
            }}
            key={inquiry.id}
            onClick={() => handleClickOpen(inquiry)}
            style={{ cursor: "pointer" }}
          >
            <Table
              sx={{
                background: "rgba(248, 248, 248, 0.767)",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "40px" }}>속성</TableCell>
                  <TableCell sx={{ width: "calc(100% - 40px)" }}>
                    {shortenWords(inquiry.title)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>내용</TableCell>
                  <TableCell>{shortenWords(inquiry.content)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    답변
                    <br />
                    완료
                    <br />
                    여부
                  </TableCell>
                  <TableCell>
                    {inquiry.reply ? (
                      <Check color="success" /> // 답변이 있는 경우 O 아이콘
                    ) : (
                      <Close color="error" /> // 답변이 없는 경우 X 아이콘
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>등록일</TableCell>
                  <TableCell>
                    {new Date(inquiry.createdAt).toLocaleString()}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>수정일</TableCell>
                  <TableCell>
                    {new Date(inquiry.updatedAt).toLocaleString()}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>삭제</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(inquiry);
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ))}
      </Box>

      {/*문의 상세 모달*/}
      {selectedInquiry && (
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth="lg" // 최대 너비를 설정 (필요에 따라 조정)
          sx={{ maxWidth: "860px", width: "90%", margin: "0 auto" }}
        >
          <DialogTitle
            sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}
          >
            {selectedInquiry.title}
          </DialogTitle>
          <DialogContent dividers>
            <Typography
              variant="body1"
              gutterBottom
              sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}
            >
              {selectedInquiry.content}
            </Typography>
          </DialogContent>

          {/*답변 존재할 때만 표시*/}
          {selectedInquiry.reply !== null && (
            <DialogContent dividers>
              <Typography variant="body1" gutterBottom>
                답변: {selectedInquiry.reply!.content}
              </Typography>

              <Typography variant="caption" color="textSecondary" gutterBottom>
                답변 생성일:{" "}
                {new Date(
                  selectedInquiry.reply!.createdAt
                ).toLocaleDateString()}
              </Typography>
              <br />
              <Typography variant="caption" color="textSecondary">
                답변 수정일:{" "}
                {new Date(
                  selectedInquiry.reply!.updatedAt
                ).toLocaleDateString()}
              </Typography>
            </DialogContent>
          )}
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              닫기
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* 문의 생성 모달 */}
      <Dialog
        open={open && !selectedInquiry}
        onClose={handleClose}
        fullWidth
        maxWidth="lg" // 최대 너비를 설정 (필요에 따라 조정)
        sx={{ maxWidth: "860px", width: "90%", margin: "0 auto" }}
      >
        <DialogTitle>문의 생성</DialogTitle>
        <DialogContent>
          <TextField
            label="제목"
            variant="outlined"
            fullWidth
            margin="normal"
            value={newInquiryTitle}
            onChange={(e) => setNewInquiryTitle(e.target.value)}
          />
          <TextField
            label="내용"
            variant="outlined"
            fullWidth
            margin="normal"
            value={newInquiryContent}
            onChange={(e) => setNewInquiryContent(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            취소
          </Button>
          <Button onClick={handleSubmitInquiry} color="primary">
            생성
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InquiryList;
